import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Typography,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  Menu,
  MenuItem
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Download as DownloadIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Description as DocumentIcon,
  MoreVert as MoreIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../../services/ApiService';
import { usePermissions } from '../../hooks/usePermissions';
import { Permission, UserRole } from '../../types/user';

interface Document {
  id: string;
  title: string;
  description: string;
  fileName: string;
  fileSize: number;
  fileType: string;
  category: 'policy' | 'template' | 'evidence' | 'report' | 'other';
  uploadedBy: string;
  uploadedAt: string;
  requirementId?: string;
  requirementTitle?: string;
  tags: string[];
}

interface DocumentListResponse {
  data: Document[];
  total: number;
}

export default function DocumentList() {
  const navigate = useNavigate();
  const { hasPermission, userRole } = usePermissions();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedDoc, setSelectedDoc] = useState<Document | null>(null);
  
  // Check permissions
  const canCreate = hasPermission(Permission.DOCUMENT_CREATE);
  const canEdit = hasPermission(Permission.DOCUMENT_EDIT);
  const canDelete = hasPermission(Permission.DOCUMENT_DELETE);
  const isDocumentManager = userRole === UserRole.DOCUMENT_MANAGER;

  useEffect(() => {
    fetchDocuments();
  }, [page, rowsPerPage]);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await ApiService.get<DocumentListResponse>('/documents', {
        page: page + 1,
        limit: rowsPerPage,
        search: searchQuery
      });
      setDocuments(response.data || []);
      setTotalCount(response.total || 0);
    } catch (error) {
      console.error('Error fetching documents:', error);
      // Use mock data if API fails
      setDocuments(getMockDocuments());
      setTotalCount(6);
    } finally {
      setLoading(false);
    }
  };

  const getMockDocuments = (): Document[] => [
    {
      id: '1',
      title: 'GDPR Privacy Policy Template',
      description: 'Template for creating GDPR-compliant privacy policies',
      fileName: 'gdpr-privacy-policy-template.docx',
      fileSize: 45678,
      fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      category: 'template',
      uploadedBy: 'Jane Smith',
      uploadedAt: '2025-05-20T10:00:00Z',
      requirementId: 'req-1',
      requirementTitle: 'Develop Website Privacy Policy',
      tags: ['GDPR', 'Privacy', 'Template']
    },
    {
      id: '2',
      title: 'Consent Form Design Mockup',
      description: 'UI/UX design mockup for consent form implementation',
      fileName: 'consent-form-mockup.pdf',
      fileSize: 234567,
      fileType: 'application/pdf',
      category: 'template',
      uploadedBy: 'John Developer',
      uploadedAt: '2025-05-21T14:30:00Z',
      requirementId: 'req-2',
      requirementTitle: 'Implement Consent Form',
      tags: ['GDPR', 'Consent', 'Design']
    },
    {
      id: '3',
      title: 'Fire Safety Equipment Register',
      description: 'Complete register of all fire safety equipment in the building',
      fileName: 'fire-safety-register.xlsx',
      fileSize: 89012,
      fileType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      category: 'evidence',
      uploadedBy: 'Robert Chen',
      uploadedAt: '2025-05-22T09:00:00Z',
      requirementId: 'req-3',
      requirementTitle: 'Fire Equipment Inspection Schedule',
      tags: ['Fire Safety', 'Building', 'Register']
    },
    {
      id: '4',
      title: 'Elevator Maintenance RFP',
      description: 'Request for proposal document for elevator maintenance services',
      fileName: 'elevator-maintenance-rfp.docx',
      fileSize: 56789,
      fileType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      category: 'policy',
      uploadedBy: 'Sarah Johnson',
      uploadedAt: '2025-05-19T16:00:00Z',
      requirementId: 'req-4',
      requirementTitle: 'Elevator Service Agreement',
      tags: ['Elevator', 'Maintenance', 'RFP']
    },
    {
      id: '5',
      title: 'Compliance Status Report Q1 2025',
      description: 'Quarterly compliance status report for executive review',
      fileName: 'compliance-report-q1-2025.pdf',
      fileSize: 345678,
      fileType: 'application/pdf',
      category: 'report',
      uploadedBy: 'Compliance Manager',
      uploadedAt: '2025-05-15T11:00:00Z',
      tags: ['Report', 'Compliance', 'Q1']
    },
    {
      id: '6',
      title: 'Data Retention Policy',
      description: 'Company-wide data retention policy document',
      fileName: 'data-retention-policy.pdf',
      fileSize: 123456,
      fileType: 'application/pdf',
      category: 'policy',
      uploadedBy: 'Legal Team',
      uploadedAt: '2025-05-18T13:00:00Z',
      tags: ['GDPR', 'Data Retention', 'Policy']
    }
  ];

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      fetchDocuments();
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, doc: Document) => {
    setAnchorEl(event.currentTarget);
    setSelectedDoc(doc);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedDoc(null);
  };

  const handleDownload = (doc: Document) => {
    // In a real app, this would trigger a download
    console.log('Downloading:', doc.fileName);
    handleMenuClose();
  };

  const handleDelete = async (doc: Document) => {
    if (window.confirm(`Are you sure you want to delete "${doc.title}"?`)) {
      try {
        await ApiService.delete<void>(`/documents/${doc.id}`);
        fetchDocuments();
      } catch (error) {
        console.error('Error deleting document:', error);
      }
    }
    handleMenuClose();
  };

  const getCategoryColor = (category: Document['category']) => {
    switch (category) {
      case 'policy':
        return 'primary';
      case 'template':
        return 'secondary';
      case 'evidence':
        return 'success';
      case 'report':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (fileType: string) => {
    // In a real app, you might have different icons for different file types
    return <DocumentIcon />;
  };

  return (
    <Box data-testid="document-list-container">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" data-testid="document-list-heading">
          Documents
        </Typography>
        {canCreate && (
          <Button
            id="upload-document-button"
            variant="contained"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => navigate('/documents/upload')}
            data-testid="upload-document-button"
          >
            Upload Document
          </Button>
        )}
      </Box>

      <Paper sx={{ mb: 2, p: 2 }}>
        <Box display="flex" gap={2}>
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleSearch}
            data-testid="document-search-input"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="outlined"
            startIcon={<FilterIcon />}
            data-testid="filter-button"
          >
            Filters
          </Button>
        </Box>
      </Paper>

      <TableContainer component={Paper} data-testid="document-table-container">
        <Table data-testid="document-table">
          <TableHead>
            <TableRow>
              <TableCell>Document</TableCell>
              <TableCell>Category</TableCell>
              <TableCell>Related To</TableCell>
              <TableCell>Size</TableCell>
              <TableCell>Uploaded</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center" data-testid="document-list-loading">
                  Loading documents...
                </TableCell>
              </TableRow>
            ) : documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" data-testid="document-list-empty">
                  No documents found
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => (
                <TableRow key={doc.id} hover data-testid={`document-row-${doc.id}`}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      {getFileIcon(doc.fileType)}
                      <Box ml={2}>
                        <Typography variant="body1" data-testid={`document-title-${doc.id}`}>
                          {doc.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" data-testid={`document-filename-${doc.id}`}>
                          {doc.fileName}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={doc.category}
                      color={getCategoryColor(doc.category)}
                      size="small"
                      data-testid={`document-category-${doc.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    {doc.requirementTitle ? (
                      <Typography variant="body2" color="text.secondary" data-testid={`document-requirement-${doc.id}`}>
                        {doc.requirementTitle}
                      </Typography>
                    ) : (
                      <span data-testid={`document-no-requirement-${doc.id}`}>-</span>
                    )}
                  </TableCell>
                  <TableCell data-testid={`document-size-${doc.id}`}>
                    {formatFileSize(doc.fileSize)}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" data-testid={`document-uploaded-by-${doc.id}`}>
                      {doc.uploadedBy}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" data-testid={`document-uploaded-date-${doc.id}`}>
                      {new Date(doc.uploadedAt).toLocaleDateString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(doc)}
                      title="Download"
                      data-testid={`document-download-${doc.id}`}
                    >
                      <DownloadIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={(e) => handleMenuClick(e, doc)}
                      title="More options"
                      data-testid={`document-menu-${doc.id}`}
                    >
                      <MoreIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          data-testid="document-pagination"
        />
      </TableContainer>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        data-testid="document-context-menu"
      >
        <MenuItem onClick={() => selectedDoc && handleDownload(selectedDoc)} data-testid="menu-download">
          <DownloadIcon sx={{ mr: 1 }} /> Download
        </MenuItem>
        <MenuItem onClick={() => selectedDoc && navigate(`/documents/${selectedDoc.id}`)} data-testid="menu-view-details">
          <DocumentIcon sx={{ mr: 1 }} /> View Details
        </MenuItem>
        {canDelete && (
          <MenuItem onClick={() => selectedDoc && handleDelete(selectedDoc)} sx={{ color: 'error.main' }} data-testid="menu-delete">
            <DeleteIcon sx={{ mr: 1 }} /> Delete
          </MenuItem>
        )}
      </Menu>
    </Box>
  );
}
